import React, { useEffect, useState } from "react";
import { Button, LoadingDots } from "web-ui";

import {
  type BulkRegistrationResponse,
  type Challenge,
  languageChallengeApi,
} from "../../api/languageChallenge";
import languages from "../../data/languages.json";
import styles from "../../styles/GlobalChallengeAdmin.module.scss";
import { isExternalChallenge } from "../../util/challenges";
import type { LanguageId } from "../../util/languages";

const BulkRegistrationForm = () => {
  // All challenges are external challenges and active
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [selectedChallengeId, setSelectedChallengeId] = useState("");
  const [emails, setEmails] = useState("");
  const [learningLanguage, setLearningLanguage] = useState<LanguageId | "">("");
  const [fromLanguage, setFromLanguage] = useState<LanguageId | "">("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState("");

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const activeChallenges = await languageChallengeApi.getChallenges();
        // Filter for external challenges only
        const externalChallenges = activeChallenges.filter(c =>
          isExternalChallenge(c.id),
        );
        setChallenges(externalChallenges);
      } catch (error) {
        // Failed to fetch challenges
      }
    };
    fetchChallenges();
  }, []);

  const languageOptions = Object.entries(languages).map(([code, lang]) => ({
    label: lang.name,
    value: code,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setResultMessage("");

    try {
      const participantList = emails
        .split(",")
        .map(email => email.trim())
        .filter(email => email.length > 0);
      const response: BulkRegistrationResponse =
        await languageChallengeApi.postBulkUserRegistration(
          participantList,
          selectedChallengeId,
          learningLanguage as LanguageId,
          fromLanguage as LanguageId,
        );

      let message = "Registration complete!\n";
      message += `✓ Successfully registered: ${response.successCount}\n`;
      if (response.alreadyRegisteredCount > 0) {
        message += `⚠ Already registered: ${response.alreadyRegisteredCount}\n`;
        message += `  User IDs: ${response.alreadyRegisteredUsers.join(", ")}\n`;
      }
      if (response.notFoundCount && response.notFoundCount > 0) {
        message += `\n✗ Failed to register: ${response.notFoundCount}\n`;
        if (response.emailsNotFound && response.emailsNotFound.length > 0) {
          message += `  Emails not found in system: ${response.emailsNotFound.join(", ")}\n`;
        }
        if (
          response.emailsRegistrationFailed &&
          response.emailsRegistrationFailed.length > 0
        ) {
          message += `  Registration failed (user found but error occurred): ${response.emailsRegistrationFailed.join(", ")}\n`;
        }
      }

      setResultMessage(message);
      setEmails("");
    } catch (error) {
      setResultMessage("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    selectedChallengeId && emails && learningLanguage && fromLanguage;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Bulk Register Participants</h2>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="challengeSelect">
            Challenge <span className={styles.required}>*</span>
          </label>
          <select
            className={styles.select}
            id="challengeSelect"
            onChange={e => setSelectedChallengeId(e.target.value)}
            value={selectedChallengeId}
          >
            <option value="">Select a challenge</option>
            {challenges.map(challenge => (
              <option key={challenge.id} value={challenge.id}>
                {challenge.id} - {challenge.description}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="emails">
            Participant Emails <span className={styles.required}>*</span>
          </label>
          <p className={styles.helpText}>
            Comma-separated list of email addresses (e.g., user1@example.com,
            user2@example.com)
          </p>
          <textarea
            className={styles.textarea}
            id="emails"
            onChange={e => setEmails(e.target.value)}
            rows={5}
            value={emails}
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="learningLanguage">
              Learning Language <span className={styles.required}>*</span>
            </label>
            <select
              className={styles.select}
              id="learningLanguage"
              onChange={e => setLearningLanguage(e.target.value as LanguageId)}
              value={learningLanguage}
            >
              <option value="">Select language</option>
              {languageOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="fromLanguage">
              From Language <span className={styles.required}>*</span>
            </label>
            <select
              className={styles.select}
              id="fromLanguage"
              onChange={e => setFromLanguage(e.target.value as LanguageId)}
              value={fromLanguage}
            >
              <option value="">Select language</option>
              {languageOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <p className={styles.helpText}>
            <strong>Note:</strong> If &quot;All Courses&quot; is selected, XP
            gained in all courses will count toward the challenge. If &quot;All
            Language Courses&quot; is selected, XP from all language courses
            will count (excludes Math, Music, and Chess). If a specific course
            is selected, only XP from that course will count. We currently only
            support all courses, all language courses, or a single course. A
            single course can be chess, math, music, or English from French.
            English from any language is not supported, and will be treated as
            all courses.
          </p>
        </div>

        <Button
          className={styles.submitButton}
          color="macaw"
          disabled={!isFormValid || isSubmitting}
          type="submit"
          variant="solid"
        >
          {isSubmitting ? (
            <LoadingDots type="button" />
          ) : (
            "Register Participants"
          )}
        </Button>

        {resultMessage && (
          <div className={styles.resultMessage}>{resultMessage}</div>
        )}
      </form>
    </div>
  );
};

export default BulkRegistrationForm;
